import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";

import {
  type DiscoverBehavior,
  type UiLinkTo,
  useUiLink,
} from "@beweegbaas/ui/components/ui/link-adapter";
import { cn } from "@beweegbaas/ui/lib/utils";

const linkVariants = cva(
  "not-button:underline-hover-style transition-none! disabled:no-underline cursor-pointer disabled:cursor-default focus-visible:outline-2 focus-visible:outline-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        primary: "text-blue-600 dark:text-white not-button:underline",
        secondary: "text-foreground/80 dark:text-zinc-300",
        muted: "text-muted-foreground hover:text-foreground",
        white: "text-white",
      },
      underline: {
        true: "not-has-data-[slot=button]:underline-style",
        false: "",
      },
      size: {
        default: "",
        sm: "text-sm",
        xs: "text-xs",
      },
    },
    compoundVariants: [
      {
        variant: "white",
        underline: true,
        className:
          "text-white hover:text-white/80 underline decoration-white/50 hover:decoration-white/30",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "default",
      underline: false,
    },
  },
);

export type LinkVariants = VariantProps<typeof linkVariants>;

export interface LinkProps extends Omit<ComponentProps<"a">, "href" | "className">, LinkVariants {
  href?: UiLinkTo;
  to?: UiLinkTo;
  /** Router-specific options, forwarded to the UiLink adapter. */
  params?: unknown;
  search?: unknown;
  preventScrollReset?: boolean;
  /** Router preload strategy; `false` opts out of the router's default hover-preload. */
  preload?: "intent" | "render" | "viewport" | false;
  discover?: DiscoverBehavior;
  disabled?: boolean;
  className?: string;
}

export function Link({
  href,
  to,
  className,
  underline,
  variant,
  size,
  disabled,
  ...props
}: LinkProps) {
  const UiLink = useUiLink();

  if (href) to = href;
  if (!to) {
    to = "#";
  }

  return (
    <UiLink
      {...props}
      to={to}
      tabIndex={disabled ? -1 : undefined}
      className={() =>
        cn(
          linkVariants({ variant, size, underline }),
          {
            "pointer-events-none": disabled,
          },
          className,
        )
      }
    />
  );
}

interface FakeLinkProps extends LinkVariants, ComponentProps<"span"> {
  className?: string;
}

export function FakeLink({ className, variant, size, underline, ...props }: FakeLinkProps) {
  return <span {...props} className={cn(linkVariants({ variant, size, underline }), className)} />;
}
